import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  ExternalLink,
  LineChart as LineChartIcon,
  Newspaper,
  RefreshCw,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import {
  compactInr,
  fullDateLabel,
  inr,
  percent,
  ratio,
} from "./format";
import {
  getMarketSnapshot,
  normalizeMarketExchange,
  normalizeMarketTicker,
} from "../../services/marketApi";
import type { MarketSnapshotResult } from "./types";

const NA = "--";
const RANGES = [
  { key: "1M", days: 30 },
  { key: "6M", days: 182 },
  { key: "1Y", days: 366 },
] as const;

function isUnavailable(value: React.ReactNode) {
  if (value == null) return true;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return !v || v === NA.toLowerCase() || v === "not available" || v === "n/a";
  }
  return false;
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  if (isUnavailable(value)) return null;
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-medium">{value}</dd>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (isUnavailable(value)) return null;
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-dashed py-2 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
}

type MarketDataProps = {
  ticker?: string | null;
  exchange?: string | null;
  companyName: string;
  customerId?: string | null;
  /** Fall back to local test snapshot if live fetch fails. */
  useTestData?: boolean;
};

export function MarketData({
  ticker,
  exchange,
  companyName,
  customerId,
  useTestData = true,
}: MarketDataProps) {
  const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("1Y");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [result, setResult] = useState<MarketSnapshotResult | null>(null);
  const requestIdRef = useRef(0);

  const normalizedTicker = normalizeMarketTicker(ticker);
  const normalizedExchange = normalizeMarketExchange(exchange);

  const loadSnapshot = async (refresh = false) => {
    if (!customerId || !companyName?.trim()) {
      setResult(null);
      setIsLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    const setBusy = refresh ? setIsRefreshing : setIsLoading;
    setBusy(true);
    if (!refresh) setResult(null);

    try {
      const next = await getMarketSnapshot({
        // Only send ticker/exchange for this customer — never reuse another company's.
        ticker: normalizedTicker || undefined,
        exchange: normalizedExchange || undefined,
        companyName: companyName.trim(),
        customerId: String(customerId),
        // Bust backend cache when identity changes or user refreshes.
        refresh: refresh || undefined,
        useTestData,
      });
      if (requestId !== requestIdRef.current) return;
      setResult(next);
    } finally {
      if (requestId === requestIdRef.current) setBusy(false);
    }
  };

  useEffect(() => {
    // Force refresh on customer/company change so prior company cache/ticker is not reused.
    void loadSnapshot(true);
    return () => {
      requestIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedTicker, normalizedExchange, companyName, customerId]);

  const snapshot = result?.status === "ready" ? result.snapshot : null;
  const quote = snapshot?.quote ?? null;
  const stats = snapshot?.fundamentals ?? null;
  const currency = quote?.currency ?? "INR";

  const chartData = useMemo(() => {
    const history = snapshot?.history ?? [];
    const clean = history
      .filter((candle) => typeof candle.close === "number" && Number.isFinite(candle.close))
      .map((candle) => ({ date: candle.date, close: candle.close as number }));
    if (!clean.length) return [];
    const days = RANGES.find((r) => r.key === range)?.days ?? 366;
    const cutoff = Date.now() - days * 86_400_000;
    const scoped = clean.filter((candle) => new Date(candle.date).getTime() >= cutoff);
    return scoped.length > 1 ? scoped : clean;
  }, [snapshot, range]);

  const yDomain = useMemo<[number, number] | undefined>(() => {
    if (!chartData.length) return undefined;
    const values = chartData.map((point) => point.close);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = (max - min || max * 0.05 || 1) * 0.08;
    return [Math.max(0, min - pad), max + pad];
  }, [chartData]);

  if (isLoading && !result) {
    return (
      <div className="mt-6 space-y-3 rounded-lg border p-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-10 w-56" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const hasContent =
    !!quote?.price ||
    !!stats ||
    !!snapshot?.news?.length ||
    chartData.length > 0;

  if (!result || result.status === "unavailable" || !hasContent) {
    return (
      <div className="mt-6 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        {result?.status === "unavailable"
          ? result.message
          : "Market data is not available for this company."}
      </div>
    );
  }

  const symbol =
    quote?.symbol ||
    (result.status === "ready" ? result.symbol : "") ||
    normalizedTicker ||
    "Market";
  const up = (quote?.change ?? 0) >= 0;
  const dayRange =
    quote?.dayLow != null && quote?.dayHigh != null
      ? `${inr(quote.dayLow, currency)} – ${inr(quote.dayHigh, currency)}`
      : NA;
  // Live quote fields only — marketCap alone may come from stats, not a price quote.
  const hasQuotePanel =
    quote != null &&
    (quote.price != null ||
      quote.open != null ||
      quote.previousClose != null ||
      quote.volume != null ||
      quote.dayLow != null ||
      quote.dayHigh != null ||
      quote.fiftyTwoWeekHigh != null ||
      quote.fiftyTwoWeekLow != null ||
      quote.change != null);

  return (
    <div className="mt-6 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold flex items-center gap-2">
              <LineChartIcon className="h-4 w-4" />
              {symbol}
            </p>
            {(quote?.exchange ?? normalizedExchange) && (
              <Badge variant="outline">
                {(quote?.exchange ?? normalizedExchange)?.toUpperCase()}
              </Badge>
            )}
            {isRefreshing && <Badge variant="outline">Refreshing…</Badge>}
          </div>
          {companyName && (
            <p className="mt-1 text-xs text-muted-foreground">{companyName}</p>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void loadSnapshot(true)}
          disabled={isRefreshing}
          title="Refresh market data"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {hasQuotePanel && (
        <>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <p className="text-2xl font-semibold">{inr(quote.price, currency)}</p>
            {(quote.change != null || quote.changePercent != null) && (
              <p
                className={`flex items-center gap-1 text-sm font-medium ${
                  up ? "text-green-600" : "text-red-600"
                }`}
              >
                {up ? (
                  <ArrowUpRight className="h-4 w-4" />
                ) : (
                  <ArrowDownRight className="h-4 w-4" />
                )}
                {quote.change === null ? NA : inr(quote.change, currency)}
                {quote.changePercent !== null &&
                  ` (${quote.changePercent.toFixed(2)}%)`}
              </p>
            )}
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Open" value={inr(quote.open, currency)} />
            <Stat
              label="Previous close"
              value={inr(quote.previousClose, currency)}
            />
            <Stat label="Day range" value={dayRange} />
            <Stat
              label="Volume"
              value={quote.volume?.toLocaleString("en-IN") ?? NA}
            />
            <Stat
              label="Market cap"
              value={compactInr(
                quote.marketCap ?? stats?.marketCap ?? null,
                currency
              )}
            />
            <Stat
              label="52-week high"
              value={inr(quote.fiftyTwoWeekHigh, currency)}
            />
            <Stat
              label="52-week low"
              value={inr(quote.fiftyTwoWeekLow, currency)}
            />
            <Stat
              label="As of"
              value={
                quote.marketTime
                  ? new Date(quote.marketTime).toLocaleString("en-IN")
                  : NA
              }
            />
          </dl>
        </>
      )}

      {(chartData.length > 0 || hasQuotePanel) && (
        <div className="mt-6 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground">
              Closing price
            </p>
            <div className="flex gap-1">
              {RANGES.map((option) => (
                <Button
                  key={option.key}
                  size="sm"
                  variant={range === option.key ? "secondary" : "ghost"}
                  className="h-7 px-2 text-xs"
                  onClick={() => setRange(option.key)}
                >
                  {option.key}
                </Button>
              ))}
            </div>
          </div>
          {chartData.length > 1 ? (
            <div className="mt-2 h-64 w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%" debounce={80}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="marketCloseFill"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="hsl(var(--primary))"
                        stopOpacity={0.28}
                      />
                      <stop
                        offset="100%"
                        stopColor="hsl(var(--primary))"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    minTickGap={40}
                    tick={{
                      fontSize: 11,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                    tickFormatter={(value: string) =>
                      new Date(value).toLocaleDateString("en-IN", {
                        month: "short",
                        year: "2-digit",
                      })
                    }
                  />
                  <YAxis
                    width={70}
                    domain={yDomain ?? ["auto", "auto"]}
                    tickLine={false}
                    axisLine={false}
                    tick={{
                      fontSize: 11,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                    tickFormatter={(value: number) =>
                      Math.round(value).toLocaleString("en-IN")
                    }
                  />
                  <Tooltip
                    cursor={{ stroke: "hsl(var(--border))" }}
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid hsl(var(--border))",
                      background: "hsl(var(--popover))",
                      fontSize: 12,
                    }}
                    labelFormatter={(value: string) => fullDateLabel(value)}
                    formatter={(value: number) => [inr(value, currency), "Close"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="close"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#marketCloseFill)"
                    dot={false}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : chartData.length === 1 ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Only one trading day returned — need a longer chart range (e.g. 1y)
              for the graph.
            </p>
          ) : null}
        </div>
      )}

      <Accordion
        type="multiple"
        defaultValue={[
          ...(stats ? ["statistics"] : []),
          ...(snapshot?.news?.length ? ["stock-news"] : []),
        ]}
        className="mt-6 border-t"
      >
        {stats && (
          <AccordionItem value="statistics">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" /> Statistics
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div>
                  <Row label="Market Cap" value={compactInr(stats.marketCap, currency)} />
                  <Row label="Enterprise Value" value={compactInr(stats.enterpriseValue, currency)} />
                  <Row label="Trailing P/E" value={ratio(stats.trailingPE)} />
                  <Row label="Forward P/E" value={ratio(stats.forwardPE)} />
                  <Row label="PEG Ratio" value={ratio(stats.pegRatio)} />
                  <Row label="Price/Sales" value={ratio(stats.priceToSales)} />
                  <Row label="Price/Book" value={ratio(stats.priceToBook)} />
                  <Row label="EV/Revenue" value={ratio(stats.evToRevenue)} />
                  <Row label="EV/EBITDA" value={ratio(stats.evToEbitda)} />
                </div>
                <div>
                  <Row label="Profit Margin" value={percent(stats.profitMargin)} />
                  <Row label="Return on Assets" value={percent(stats.returnOnAssets)} />
                  <Row label="Return on Equity" value={percent(stats.returnOnEquity)} />
                  <Row label="Revenue" value={compactInr(stats.revenue, currency)} />
                  <Row label="Net Income" value={compactInr(stats.netIncome, currency)} />
                  <Row label="Diluted EPS" value={ratio(stats.dilutedEps)} />
                  <Row label="Total Cash" value={compactInr(stats.totalCash, currency)} />
                  <Row label="Debt/Equity" value={ratio(stats.debtToEquity)} />
                  <Row
                    label="Levered Free Cash Flow"
                    value={compactInr(stats.leveredFreeCashFlow, currency)}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {!!snapshot?.news?.length && (
          <AccordionItem value="stock-news">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Newspaper className="h-4 w-4" /> News ({snapshot.news.length})
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-3">
                {snapshot.news.map((item, index) => (
                  <li key={`${item.url}-${index}`} className="rounded-md border p-3">
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-start gap-2 text-sm font-medium text-primary hover:underline"
                    >
                      <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{item.title}</span>
                    </a>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {[item.publisher, item.publishedAt ? fullDateLabel(item.publishedAt) : null]
                        .filter(Boolean)
                        .join(" • ") || "Source not listed"}
                    </p>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}
